import moment from 'moment';

interface SchedulingTimeParams {
  isStartDate: boolean;
  watchStartDate?: Date;
  watchEndDate?: Date;
  watchStartTime?: Date;
  watchTimeZone: number;
}

const getAdjustedCurrentTime = (watchTimeZone: number) => {
  const now = new Date();
  const localOffset = now.getTimezoneOffset() / 60;
  if (localOffset === -watchTimeZone) return now;

  const timezoneDifference = watchTimeZone - -localOffset;
  const adjustedTime = moment(now).add(timezoneDifference, 'hours');

  return adjustedTime.toDate();
};

const getMinimumAllowedStartTime = ({
  watchStartDate,
  watchTimeZone,
}: Pick<SchedulingTimeParams, 'watchStartDate' | 'watchTimeZone'>) => {
  if (!watchStartDate) return;

  const adjustedCurrentTime = getAdjustedCurrentTime(watchTimeZone);
  const startDateMoment = moment(watchStartDate).startOf('day');
  const adjustedCurrentDateMoment = moment(adjustedCurrentTime).startOf('day');

  const isStartDateAfterCurrentDate = startDateMoment.isAfter(adjustedCurrentDateMoment, 'day');
  const isAdjustedCurrentTimeBeforeStartDate = moment(adjustedCurrentTime).isBefore(startDateMoment);

  if (isStartDateAfterCurrentDate && isAdjustedCurrentTimeBeforeStartDate) return;

  return moment(adjustedCurrentTime).add(6, 'minutes').toDate();
};

const getMinimumAllowedEndTime = ({
  watchEndDate,
  watchStartTime,
  watchStartDate,
  watchTimeZone,
}: Pick<SchedulingTimeParams, 'watchEndDate' | 'watchStartTime' | 'watchStartDate' | 'watchTimeZone'>) => {
  if (!watchEndDate) return;

  const adjustedCurrentTime = getAdjustedCurrentTime(watchTimeZone);
  const baseTime = watchStartTime ? watchStartTime : adjustedCurrentTime;
  const minimumTime = moment(baseTime).add(1, 'minute').toDate();
  const endDateMoment = moment(watchEndDate).startOf('day');
  const startDateMoment = watchStartDate ? moment(watchStartDate).startOf('day') : null;
  const isSameDay = startDateMoment && endDateMoment.isSame(startDateMoment, 'day');
  const minimumTimeDateMoment = moment(minimumTime).startOf('day');
  const isEndDateSameAsMinimumTimeDate = endDateMoment.isSame(minimumTimeDateMoment, 'day');

  if (isSameDay || isEndDateSameAsMinimumTimeDate) return minimumTime;
};

const getMinimumAllowedStartDate = ({ watchTimeZone }: Pick<SchedulingTimeParams, 'watchTimeZone'>) => {
  const adjustedCurrentTime = getAdjustedCurrentTime(watchTimeZone);

  return moment(adjustedCurrentTime).startOf('day').toDate();
};

const getMinimumAllowedEndDate = ({
  watchStartDate,
  watchTimeZone,
}: Pick<SchedulingTimeParams, 'watchStartDate' | 'watchTimeZone'>) => {
  if (watchStartDate) return moment(watchStartDate).startOf('day').toDate();

  const adjustedCurrentTime = getAdjustedCurrentTime(watchTimeZone);

  return moment(adjustedCurrentTime).startOf('day').toDate();
};

export const getMinimumAllowedDateAndTime = ({
  watchStartDate,
  watchEndDate,
  watchStartTime,
  watchTimeZone,
  isStartDate,
}: SchedulingTimeParams) => {
  if (isStartDate) {
    const minimumAllowedDate = getMinimumAllowedStartDate({ watchTimeZone });
    const minimumAllowedTime = getMinimumAllowedStartTime({ watchStartDate, watchTimeZone });

    return { minimumAllowedTime, minimumAllowedDate };
  }

  const minimumAllowedDate = getMinimumAllowedEndDate({ watchStartDate, watchTimeZone });
  const minimumAllowedTime = getMinimumAllowedEndTime({
    watchEndDate,
    watchStartTime,
    watchStartDate,
    watchTimeZone,
  });

  return { minimumAllowedTime, minimumAllowedDate };
};
